'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Add, Dots, Remove } from '@repo/ui/icons';
import { useState } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/common/overlays';
import { useChainNavigation } from '@/hooks/useChainNavigation';
import { trackPositionManagerActionSelected } from '@/lib/analytics/client/trackers/checkout-funnel';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '../dialogOptions';
import { VaultV2PositionManagerDepositDialog } from './VaultV2PositionManagerDepositDialog';
import { VaultV2PositionManagerWithdrawDialog } from './VaultV2PositionManagerWithdrawDialog';
interface VaultV2PositionManagerProps {
  vaultAddress: Address;
  chainId: ChainId;
  /** The vault's asset: sizes the deposit dialog's loading skeleton. */
  assetAddress?: Address;
  enableDeposit?: boolean;
  enableWithdraw?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'center' | 'end' | 'start';
}
const VaultV2PositionManager = t0 => {
  const $ = _c(31);
  const {
    vaultAddress,
    chainId,
    assetAddress,
    enableDeposit: t1,
    enableWithdraw: t2,
    side: t3,
    align: t4
  } = t0;
  const enableDeposit = t1 === undefined ? true : t1;
  const enableWithdraw = t2 === undefined ? true : t2;
  const side = t3 === undefined ? "top" : t3;
  const align = t4 === undefined ? "end" : t4;
  const {
    positionManagerDepositIntoVault: depositVault,
    positionManagerWithdrawVaultDeposit: withdrawVault
  } = useFeatureFlags();
  let t5;
  if ($[0] !== depositVault || $[1] !== withdrawVault) {
    t5 = {
      depositVault,
      withdrawVault
    };
    $[0] = depositVault;
    $[1] = withdrawVault;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const featureFlags = t5;
  const {
    toggleDialog
  } = useDialogContext();
  const [isOpen, setIsOpen] = useState(false);
  let t6;
  if ($[3] !== chainId) {
    t6 = {
      chainId
    };
    $[3] = chainId;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    syncChainSwitch
  } = useChainNavigation(t6);
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = open => {
      setIsOpen(open);
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      display: "flex"
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DropdownMenuTrigger asChild={true}><div style={t8}><Tooltip><TooltipTrigger asChild={true}><Button data-testid="vault-v2-position-manager-button" size="iconSm" variant="ghost" aria-label="Manage your position" onClick={_temp}><Dots /></Button></TooltipTrigger><TooltipContent>Manage your position</TooltipContent></Tooltip></div></DropdownMenuTrigger>;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== assetAddress || $[9] !== chainId || $[10] !== enableDeposit || $[11] !== featureFlags.depositVault || $[12] !== syncChainSwitch || $[13] !== toggleDialog || $[14] !== vaultAddress) {
    t10 = featureFlags.depositVault && <DropdownMenuItem disabled={!enableDeposit} data-testid="vault-v2-deposit-into-vault" onClick={_temp2} onSelect={() => {
      trackPositionManagerActionSelected({
        action: "deposit",
        positionType: "vaultV2",
        chainId
      });
      syncChainSwitch();
      toggleDialog(<VaultV2PositionManagerDepositDialog chainId={chainId} vaultAddress={vaultAddress} assetAddress={assetAddress} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        maxWidth: "540px"
      });
    }}><Icon className="text-foreground" icon={<Add />} />Deposit</DropdownMenuItem>;
    $[8] = assetAddress;
    $[9] = chainId;
    $[10] = enableDeposit;
    $[11] = featureFlags.depositVault;
    $[12] = syncChainSwitch;
    $[13] = toggleDialog;
    $[14] = vaultAddress;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== chainId || $[17] !== enableWithdraw || $[18] !== featureFlags.withdrawVault || $[19] !== syncChainSwitch || $[20] !== toggleDialog || $[21] !== vaultAddress) {
    t11 = featureFlags.withdrawVault && <DropdownMenuItem data-testid="vault-v2-withdraw-vault-deposit" disabled={!enableWithdraw} onClick={_temp3} onSelect={() => {
      trackPositionManagerActionSelected({
        action: "withdraw",
        positionType: "vaultV2",
        chainId
      });
      syncChainSwitch();
      toggleDialog(<VaultV2PositionManagerWithdrawDialog chainId={chainId} vaultAddress={vaultAddress} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        maxWidth: "540px"
      });
    }}><Icon className="text-foreground" icon={<Remove />} />Withdraw</DropdownMenuItem>;
    $[16] = chainId;
    $[17] = enableWithdraw;
    $[18] = featureFlags.withdrawVault;
    $[19] = syncChainSwitch;
    $[20] = toggleDialog;
    $[21] = vaultAddress;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== align || $[24] !== side || $[25] !== t10 || $[26] !== t11) {
    t12 = <DropdownMenuContent align={align} side={side} sideOffset={7}>{t10}{t11}</DropdownMenuContent>;
    $[23] = align;
    $[24] = side;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isOpen || $[29] !== t12) {
    t13 = <DropdownMenu open={isOpen} onOpenChange={t7}>{t9}{t12}</DropdownMenu>;
    $[28] = isOpen;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
};
export default VaultV2PositionManager;
function _temp(event) {
  event.stopPropagation();
  event.preventDefault();
}
function _temp2(event_0) {
  event_0.stopPropagation();
}
function _temp3(event_1) {
  event_1.stopPropagation();
}